import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc,
} from "firebase/firestore";
import { auth, firestore } from "../firebase/firebaseConfig";

/**
 * Validates that a userId is provided.
 * @param {string} userId - The unique ID of the user.
 * @throws Will throw an error if the userId is invalid.
 */
const validateUserId = (userId) => {
  if (!userId) {
    console.error("[validateUserId] Error: User ID is required.");
    throw new Error("User ID is required.");
  }
};

/**
 * Fetches the active subscription for the given user ID.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<Object|null>} The active subscription or null if none found.
 */
export const getActiveSubscription = async (userId) => {
  validateUserId(userId);
  return fetchSubscriptions(userId, { status: "active" });
};

/**
 * Fetches the current subscription for a given user ID.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<Object|null>} The current subscription details or null if none found.
 */
export const getUserSubscription = async (userId) => {
  try {
    if (!userId) {
      throw new Error("User ID is required to fetch subscription details.");
    }

    // Query Firestore for active subscriptions for the given user
    const subscriptionsRef = collection(firestore, "subscriptions");
    const activeSubscriptionQuery = query(
      subscriptionsRef,
      where("userId", "==", userId),
      where("status", "==", "active")
    );

    const querySnapshot = await getDocs(activeSubscriptionQuery);

    if (querySnapshot.empty) {
      console.log("No active subscriptions found for the user.");
      return null;
    }

    // Assuming only one active subscription per user
    const subscriptionData = querySnapshot.docs[0].data();
    return {
      id: querySnapshot.docs[0].id,
      ...subscriptionData,
    };
  } catch (error) {
    console.error("Error fetching user subscription:", error);
    throw new Error("Failed to fetch user subscription. Please try again.");
  }
};

// Additional exports if needed...

/**
 * Fetches subscriptions based on query parameters.
 * @param {string} userId - The unique ID of the user.
 * @param {Object} conditions - Query conditions (e.g., { status: "active" }).
 * @returns {Promise<Object|null>} The subscription data or null if none found.
 */
const fetchSubscriptions = async (userId, conditions = {}) => {
  try {
    console.log("[fetchSubscriptions] Fetching subscriptions for userId:", userId);
    validateUserId(userId);

    // Reference the `subscriptions` collection
    const subscriptionsRef = collection(firestore, "subscriptions");
    const conditionsArray = [where("userId", "==", userId)];

    // Add conditions to query
    for (const [key, value] of Object.entries(conditions)) {
      conditionsArray.push(where(key, "==", value));
    }

    // Create the query
    const subscriptionsQuery = query(subscriptionsRef, ...conditionsArray);
    console.log("[fetchSubscriptions] Query created with conditions:", conditions);

    // Execute the query
    const querySnapshot = await getDocs(subscriptionsQuery);
    console.log(`[fetchSubscriptions] Query executed. Found ${querySnapshot.size} documents.`);

    if (querySnapshot.empty) {
      console.warn("[fetchSubscriptions] No matching subscriptions found.");
      return null;
    }

    // Assuming only one matching subscription per user
    const subscriptionData = querySnapshot.docs[0].data();
    console.log("[fetchSubscriptions] Subscription data retrieved:", subscriptionData);

    return {
      id: querySnapshot.docs[0].id,
      ...subscriptionData,
    };
  } catch (error) {
    console.error("[fetchSubscriptions] Error fetching subscriptions:", error);
    throw new Error("Failed to fetch subscriptions.");
  }
};

/**
 * Fetches a user's payment history, sorted by date.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<Object[]>} An array of payment records.
 */
export const getBillingHistory = async (userId) => {
  validateUserId(userId);
  try {
    console.log(`[getBillingHistory] Start fetching billing history for userId: ${userId}`);
    validateUserId(userId);

    // Reference the `payments` collection
    const paymentsRef = collection(firestore, "payments");
    console.log("[getBillingHistory] Payments collection reference created.");

    // Create the query
    const paymentsQuery = query(
      paymentsRef,
      where("userId", "==", userId),
      orderBy("paymentDate", "desc")
    );
    console.log("[getBillingHistory] Query created for userId:", userId);

    // Execute the query
    const querySnapshot = await getDocs(paymentsQuery);
    console.log(`[getBillingHistory] Query executed. Found ${querySnapshot.size} documents.`);

    // Map the results
    const billingHistory = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log("[getBillingHistory] Billing history mapped successfully:", billingHistory);

    return billingHistory;
  } catch (error) {
    console.error("[getBillingHistory] Error fetching billing history:", error);
    throw new Error("Failed to fetch billing history. Please try again.");
  }
};

/**
 * Fetches a document from Firestore by its ID.
 * @param {string} collectionName - Name of the Firestore collection.
 * @param {string} docId - ID of the document to fetch.
 * @returns {Promise<Object|null>} The document data or null if not found.
 */
const fetchDocumentById = async (collectionName, docId) => {
  try {
    const docRef = doc(firestore, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.warn(`[fetchDocumentById] Document not found in "${collectionName}" with ID: ${docId}`);
      return null;
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error(`[fetchDocumentById] Error fetching document from "${collectionName}":`, error);
    throw new Error("Failed to fetch document.");
  }
};

/**
 * Fetch multiple documents from Firestore with conditions.
 * @param {string} collectionName - Name of the Firestore collection.
 * @param {Array} conditions - Array of query conditions (e.g., [where("field", "==", value)]).
 * @returns {Promise<Array>} An array of document data objects.
 */
const fetchDocumentsWithConditions = async (collectionName, conditions = []) => {
  try {
    const collectionRef = collection(firestore, collectionName);
    const queryRef = query(collectionRef, ...conditions);
    const querySnapshot = await getDocs(queryRef);

    if (querySnapshot.empty) {
      console.warn(`[fetchDocumentsWithConditions] No documents found in collection "${collectionName}" with the provided conditions.`);
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`[fetchDocumentsWithConditions] Error querying collection "${collectionName}":`, error);
    throw new Error("Failed to fetch documents. Please try again.");
  }
};

/**
 * Create a new document in Firestore.
 * @param {string} collectionName - Name of the Firestore collection.
 * @param {Object} data - The data to store in the new document.
 * @returns {Promise<Object>} The created document with its ID.
 */
const createDocument = async (collectionName, data) => {
  try {
    const collectionRef = collection(firestore, collectionName);
    const docRef = await addDoc(collectionRef, data);
    return { id: docRef.id, ...data };
  } catch (error) {
    console.error(`[createDocument] Error creating document in collection "${collectionName}":`, error);
    throw new Error("Failed to create document. Please try again.");
  }
};

/**
 * Count the number of transactions for a user in the current billing cycle.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<number>} The number of transactions.
 */
export const getTradeCount = async (userId) => {
  validateUserId(userId);

  // Fetch trades where the user is the buyer
  const buyerTransactions = await fetchDocumentsWithConditions("trades", [
    where("buyerId", "==", userId),
  ]);

  // Fetch trades where the user is the seller
  const sellerTransactions = await fetchDocumentsWithConditions("trades", [
    where("sellerId", "==", userId),
  ]);

  // Combine both results and return the count
  return buyerTransactions.length + sellerTransactions.length;
};

/**
 * Count the number of active listings for a user.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<number>} The number of active listings.
 */
export const getActiveListings = async (userId) => {
  try {
    const activeListings = await fetchDocumentsWithConditions("listings", [
      where("userId", "==", userId),
      where("status", "in", ["active", "matched"]),
    ]);
    return activeListings.length;
  } catch (error) {
    console.error("Error fetching active listings:", error);
    return 0; // Return 0 if there's an error to ensure fallback
  }
};


/**
 * Fetches a user's trades where they are either a buyer or seller.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<Object[]>} An array of trades.
 */
export const getUserTrades = async (userId) => {
  validateUserId(userId);

  try {
    return await fetchDocumentsWithConditions("trades", [
      where("buyerId", "==", userId),
      where("sellerId", "==", userId),
    ]);
  } catch (error) {
    console.error("[getUserTrades] Error fetching trades:", error);
    throw new Error("Failed to fetch user trades.");
  }
};
